<template>
  <PopupModal :open="open" has-close-button @close="close">
    <template #body>
      <div>
        <div>
          <AssistantSmiling class="mx-auto mb-8" />
        </div>
        <div class="text-large mb-8">{{ hint }}</div>
        <div v-if="!isPassFailChallenge">
          <inputElement
            id="progress"
            v-model="progress"
            :label="placeholder"
            :required="true"
            type="number"
            min="0"
            :step="progressInputStep"
          />
        </div>
        <div>{{ error }}</div>
      </div>
    </template>
    <template #footer>
      <div
        v-if="!isPassFailChallenge"
        class="flex flex-row w-full justify-center gap-4"
      >
        <div class="text-center">
          <ButtonElement
            :label="
              t('custom.challenge.track.progress.negative.button.caption')
            "
            :disabled-visible="true"
            @click="close"
          />
        </div>
        <div class="text-center">
          <ButtonElement
            :label="
              t('custom.challenge.track.progress.positive.button.caption')
            "
            :disabled="!isValidProgress"
            :function-disabled="!isValidProgress"
            @click="save"
          />
        </div>
      </div>
      <div
        v-else-if="isPassFailChallenge"
        class="flex flex-row w-full justify-center gap-4"
      >
        <div class="text-center">
          <ButtonElement
            class="bg-white"
            :label="t('custom.challenge.track.yes.no.no.button.caption')"
            @click="savePassFailChallenge(0)"
          />
        </div>
        <div class="text-center">
          <ButtonElement
            class="bg-white"
            :label="t('custom.challenge.track.yes.no.yes.button.caption')"
            @click="savePassFailChallenge(1)"
          />
        </div>
      </div>
    </template>
  </PopupModal>
</template>
<script setup lang="ts">
import PopupModal from "../layout/PopupModal.vue";
import InputElement from "../form/InputElement.vue";
import ButtonElement from "../form/ButtonElement.vue";
import AssistantSmiling from "../icon/AssistantSmiling.vue";
import { updateProgress } from "../../../api/v2/custom-challenge.ts";
import { useUserStore } from "../../../store/v2/user";
import { useI18n } from "vue-i18n";
import { updateCompetitiveProgress } from "../../../api/v2/competitive-challenge.ts";
import { ProgressValueType } from "../../../enum/v2/progress-value-type";

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  isPassFailChallenge: {
    type: Boolean,
  },
  hint: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  date: {
    type: String,
    default: "",
  },
  id: {
    type: [Number, String],
  },
  limit: {
    type: Number,
  },
  isCompetitiveChallenge: {
    type: Boolean,
    default: false,
  },
  initialValue: {
    type: [Number, String],
  },
  progressValueType: {
    type: String as PropType<ProgressValueType>,
  },
});

const user = useUserStore();
const emit = defineEmits(["close", "updateHistory"]);
const progress = ref(formatInitialProgress(props.initialValue));
const error = ref();
const { t } = useI18n();

const isStepsProgress = computed(
  () => props.progressValueType === ProgressValueType.Steps
);
const isKilometerProgress = computed(
  () => props.progressValueType === ProgressValueType.Kilometers
);
const progressInputStep = computed(() => {
  if (isStepsProgress.value) {
    return "1";
  }

  return isKilometerProgress.value ? "0.01" : "any";
});

const isValidProgress = computed(() => {
  const num = Number(progress.value);
  const isValidNumber =
    progress.value !== undefined &&
    progress.value !== "" &&
    !isNaN(num) &&
    num >= 0;

  if (!isValidNumber) {
    return false;
  }

  if (isStepsProgress.value) {
    return Number.isInteger(num);
  }

  return true;
});

async function save() {
  const progressValue = Number(progress.value);

  if (!isValidProgress.value) {
    error.value = t("custom.challenge.track.progress.error");
    return;
  }

  if (props.limit && progressValue > props.limit) {
    error.value = t("custom.challenge.track.progress.limit.error", {
      s: props.limit,
    });
    return;
  }
  if (progressValue < 0) {
    error.value = t("custom.challenge.track.progress.error");
    return;
  }

  let res = null;
  if (!props.isCompetitiveChallenge) {
    res = await updateProgress(
      props.id,
      user.externalUserId,
      progressValue,
      props.date
    ).then(() => {
      emit("close");
      emit("updateHistory", progressValue);
    });
  } else {
    res = await updateCompetitiveProgress(
      props.id,
      user.externalUserId,
      progressValue,
      props.date
    ).then(() => {
      emit("close");
      emit("updateHistory", progressValue);
    });
  }
}

function formatInitialProgress(value: number | string | undefined): string {
  if (value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "";
  }

  if (props.progressValueType === ProgressValueType.Steps) {
    return String(Math.ceil(numericValue));
  }

  if (props.progressValueType === ProgressValueType.Kilometers) {
    return numericValue.toFixed(2);
  }

  return String(value);
}

function savePassFailChallenge(passed: number) {
  updateProgress(props.id, user.externalUserId, passed, props.date).then(() => {
    emit("close");
    emit("updateHistory", passed);
  });
}

function close() {
  emit("close");
}
</script>
